import { supabase } from '@/lib/supabase';
import type { Build, BuildWithDetails, BuildFormData } from '@/types';
import { r2Url } from '@/lib/r2';

// Builds — uses v_builds_availability view (created by 09_VIEWS_AND_COMPAT.sql)
export async function getBuilds(options?: {
  search?: string;
  familyId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}) {
  let query = supabase
    .from('v_builds_availability')
    .select('*', { count: 'exact' });

  if (options?.search) {
    query = query.or(`sku.ilike.%${options.search}%,description.ilike.%${options.search}%`);
  }
  if (options?.status) {
    query = query.eq('status', options.status);
  }
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }
  query = query.order('sku');

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data as BuildWithDetails[], count };
}

export async function getBuild(id: string) {
  const { data, error } = await supabase
    .from('v_builds_availability')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as BuildWithDetails;
}

export async function getBuildBySku(sku: string) {
  const { data, error } = await supabase
    .from('builds')
    .select('*')
    .eq('sku', sku)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data as Build | null;
}

export async function createBuild(build: BuildFormData) {
  // Only insert fields that exist in the new schema
  const { data, error } = await supabase
    .from('builds')
    .insert({ sku: build.sku, description: build.description, status: build.status })
    .select()
    .single();
  if (error) throw error;
  return data as Build;
}

export async function updateBuild(id: string, updates: Partial<BuildFormData>) {
  const patch: Partial<Build> = {};
  if (updates.sku) patch.sku = updates.sku;
  if (updates.description) patch.description = updates.description;
  if (updates.status) patch.status = updates.status;

  const { data, error } = await supabase
    .from('builds')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Build;
}

export async function deleteBuild(id: string) {
  const { error } = await supabase.from('builds').delete().eq('id', id);
  if (error) throw error;
}

// Build Components (BOM) — not available in new schema, return empty
export async function getBuildComponents(_buildId: string) {
  return [];
}

export async function addBuildComponent(
  _buildId: string, _componentId: string, _quantity: number, _partType: string
) {
  throw new Error('BOM management is not available in the current schema version.');
}

export async function updateBuildComponent(_id: string, _updates: unknown) {
  throw new Error('BOM management is not available in the current schema version.');
}

export async function removeBuildComponent(_id: string, _buildId: string) {
  throw new Error('BOM management is not available in the current schema version.');
}

// Build Families — returns empty (no families in new schema)
export async function getBuildFamilies() {
  return [];
}

// Clients — returns empty (no clients in new schema)
export async function getClients() {
  return [];
}

export async function createClient(_client: unknown) {
  throw new Error('Client management is not available in the current schema version.');
}

// Max buildable — not calculable without BOM, return 0
export async function getMaxBuildable(_buildId: string): Promise<number> {
  return 0;
}

export async function checkBuildAvailability(_buildId: string, _quantity: number) {
  return { canBuild: true, issues: [] };
}

/**
 * One representative image for a build, as a public R2 URL.
 *
 * Same precedence the build detail page renders with: a build's own media wins,
 * and the case from its BOM only stands in when the build has no photo of its
 * own. Kept to the fewest queries that can answer that, so a page can show a
 * thumbnail without pulling the full BOM and media galleries BuildDetailPage
 * loads.
 *
 * buildSku is what makes the case fallback complete. A BOM is stored one of two
 * ways - build_components rows, or a comma-separated build_sku on the component
 * itself - and BuildDetailPage tries both. Resolving only the first here left
 * every build on the older shape with no image at all, which is exactly the
 * builds whose only photo is the case.
 *
 * Returns null when neither exists, which is the normal state for a build
 * nobody has photographed yet. Query errors also return null rather than
 * throwing: this backs a decorative thumbnail, and a missing image must never
 * take a page down with it.
 */
export async function fetchBuildPrimaryImage(
  buildId: string,
  buildSku?: string | null,
): Promise<string | null> {
  const { data: own } = await supabase
    .from('build_media')
    .select('file_path')
    .eq('build_id', buildId)
    .order('created_at', { ascending: true })
    .limit(1);
  if (own?.[0]?.file_path) return r2Url(`build-media/${own[0].file_path}`);

  const componentId = await findCaseComponentId(buildId, buildSku);
  if (!componentId) return null;

  const { data: caseMedia } = await supabase
    .from('component_media')
    .select('file_path')
    .eq('component_id', componentId)
    .order('created_at', { ascending: true })
    .limit(1);
  // component_media shares the build-media/ prefix in R2 - not a typo, it is
  // the same bucket path BuildDetailPage builds case URLs from.
  return caseMedia?.[0]?.file_path ? r2Url(`build-media/${caseMedia[0].file_path}`) : null;
}

/** Both spellings a case is filed under, in build_components and in components.category. */
const isCaseType = (t: string | null | undefined): boolean =>
  ['case', 'cas'].includes((t ?? '').toLowerCase());

/**
 * The component id of a build's case, over either BOM shape. Mirrors the two
 * paths in BuildDetailPage's BOM query, in the same order.
 */
async function findCaseComponentId(buildId: string, buildSku?: string | null): Promise<string | null> {
  const { data: bom } = await supabase
    .from('build_components')
    .select('component_sku, component_type')
    .eq('build_id', buildId);

  const caseSku = (bom ?? []).find(r => isCaseType(r.component_type))?.component_sku;
  if (caseSku) {
    const { data: comp } = await supabase
      .from('components')
      .select('id')
      .eq('part_number', caseSku)
      .maybeSingle();
    if (comp) return comp.id as string;
  }

  // Older shape: the component carries a comma-separated list of the builds it
  // belongs to. ilike narrows it server-side, then membership is checked exactly
  // so AOI-PA-G6 never matches AOI-PA-G60.
  if (!buildSku) return null;
  const { data: comps } = await supabase
    .from('components')
    .select('id, category, build_sku')
    .ilike('build_sku', `%${buildSku}%`);

  const match = (comps ?? []).find(c => {
    if (!isCaseType(c.category)) return false;
    return (c.build_sku ?? '')
      .split(',')
      .map((x: string) => x.trim().replace(/-EOL$/i, ''))
      .includes(buildSku);
  });
  return match ? (match.id as string) : null;
}
